/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { AfterViewInit, Component, Inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectUsbDeviceName } from '../../+state/usb.selectors';
import { FirmwareUpgradeService } from '../services/firmware-upgrade.service';
import { SamFirmwareUpgradeService } from '../../sam/services/sam-firmware-upgrade.service';
import { RtiFirmwareUpgradeService } from '../../rti/services/rti-firmware-upgrade.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IconModule, InputGroupModule, InputModule, ModalModule, MSI_MODAL_DATA, MsiModalRef, ProgressBarModule, SelectModule, TabsModule } from '@msi/cobalt';
import { NgLetModule } from 'ng-let';
import { FirmwareUpgradeMode } from '../../../configuration/model/ui-settings';
import { firmwareChecked } from '../../+state/usb.actions';

@Component({
    selector: 'msi-firmware-update',
    templateUrl: './firmware-update.component.html',
    styleUrl: './firmware-update.component.scss',
    imports: [CommonModule, NgLetModule, ModalModule, TabsModule, InputGroupModule, InputModule, SelectModule, FormsModule, ProgressBarModule, IconModule]
})
export class FirmwareUpdateComponent implements OnInit, AfterViewInit, OnDestroy {
    public readonly FirmwareUpgradeMode = FirmwareUpgradeMode;
    public mode = signal<FirmwareUpgradeMode>(FirmwareUpgradeMode.MANUAL);
    public service: FirmwareUpgradeService;
    public firmware: string[];
    public selectedFirmware: string;
    public selectedFile: File;
    private unsubscribe$ = new Subject<void>();

    constructor(
        private store: Store,
        private injector: Injector,
        public modalRef: MsiModalRef,
        @Inject(MSI_MODAL_DATA) public data: { mode: FirmwareUpgradeMode }
    ) {
        this.mode.set(this.data?.mode);
    }

    public ngOnInit() {
        this.store
            .select(selectUsbDeviceName)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((device) => {
                if (device === 'SAM') {
                    this.service = <SamFirmwareUpgradeService>this.injector.get(SamFirmwareUpgradeService);
                    this.firmware = Object.keys(this.service.FIRMWARE);
                } else if (device === 'RTI') {
                    this.service = <RtiFirmwareUpgradeService>this.injector.get(RtiFirmwareUpgradeService);
                    this.firmware = Object.keys(this.service.FIRMWARE);
                }
                this.selectedFirmware = this.firmware.sort()[this.firmware.length - 1];
            });
    }

    ngAfterViewInit(): void {
        if (this.mode() === FirmwareUpgradeMode.AUTOMATIC) {
            this.doUpdate()
                .then(() => console.debug('Automatic firmware update complete'))
                .catch(() => console.error('Automatic firmware update failed.'));
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    protected async doUpdate() {
        if (this.selectedFirmware) {
            await this.service?.selectHostedFirmware(this.selectedFirmware);
            if (this.mode() === FirmwareUpgradeMode.AUTOMATIC) {
                setTimeout(() => this.reload(), 5000);
            }
        } else if (this.selectedFile) {
            await this.service?.manualFirmware(this.selectedFile);
        }
    }

    protected doCancel() {
        this.service?.cancelFirmwareUpdate();
    }

    protected onFileSelect($event: Event) {
        this.selectedFile = ($event.currentTarget as HTMLInputElement).files[0];
    }

    protected onHostedSelectionChange() {
        this.selectedFile = this.selectedFirmware ? undefined : this.selectedFile;
    }

    protected clear() {
        this.selectedFirmware = undefined;
        this.selectedFile = undefined;
    }

    protected reload() {
        window.location.reload();
    }

    closeWindow() {
        this.store.dispatch(firmwareChecked( { device: this.service.service.DEVICE_NAME }));
        this.modalRef.close();
    }
}
